import * as React from 'react';

import * as Sentry from '@sentry/react';

import { tupleToStringKey } from 'js/lib/stringKeyTuple';
import { get as getUser } from 'js/lib/user';

import { getCoachItemType as getCoachItemTypeName, pluginIsACoachItem } from 'bundles/coach-item/common/lib';
import type { CoachItemType } from 'bundles/coach-item/common/types';
import type { Item } from 'bundles/learner-progress/types/Item';
import { useNaptime } from 'bundles/naptimejs';
import type { InjectedNaptime } from 'bundles/naptimejs';
import OnDemandWidgetSessionsV1 from 'bundles/naptimejs/resources/onDemandWidgetSessionsV1';

const getCoachItemType = async (naptime: InjectedNaptime, id: string) => {
  try {
    const naptimeResponse = await naptime.executeMutation(
      OnDemandWidgetSessionsV1.action(
        'getIframeTitle',
        {},
        {
          id,
        }
      )
    );

    const { data } = naptimeResponse;

    const isPluginCoachItem = pluginIsACoachItem(data);

    const coachItemTypeName = isPluginCoachItem ? getCoachItemTypeName(data) : undefined;
    return coachItemTypeName;
  } catch (error) {
    Sentry.captureException(error);
    return undefined;
  }
};

const useCoachItemType = (computedItem: Item): CoachItemType | undefined => {
  const [coachItemType, setCoachItemType] = React.useState<CoachItemType | undefined>(undefined);

  const { naptime } = useNaptime(() => ({}));

  const { courseId, id: itemId } = computedItem;
  const userId = getUser().id;
  const id = tupleToStringKey([userId.toString(), courseId, itemId]);

  // @ts-expect-error ts-migrate(7009) FIXME: update ContentSummary type to include the 'ungradedWidget' typeName
  const isUngradedPlugin = computedItem.contentSummary.typeName === 'ungradedWidget';

  React.useEffect(() => {
    const fetchIframeTitle = async () => {
      if (isUngradedPlugin) {
        const coachItemTypeName = await getCoachItemType(naptime, id);
        setCoachItemType(coachItemTypeName);
      }
    };

    fetchIframeTitle();
  }, [naptime, id, isUngradedPlugin]);

  return coachItemType;
};

export default useCoachItemType;
